import { tryJsonParse } from "./string.ts";

const UNAVAILABLE_MESSAGE = "MixItOne could not be reached right now. Check your connection and try again.";

const TECHNICAL_ERROR_PATTERNS: RegExp[] = [
  /edge function returned a non-2xx/i,
  /functionshttperror/i,
  /failed to fetch/i,
  /fetch failed/i,
  /network error/i,
  /econnrefused/i,
  /connection refused/i,
  /502\b/,
  /503\b/,
  /504\b/,
];

function messageFromResponseBody(data: unknown): string | null {
  if (!data) return null;
  if (typeof data === "string") {
    const parsed = tryJsonParse(data) as { error?: unknown; message?: unknown } | null;
    if (typeof parsed?.error === "string") return parsed.error;
    if (typeof parsed?.message === "string") return parsed.message;
    return null;
  }
  if (typeof data === "object") {
    const record = data as { error?: unknown; message?: unknown };
    if (typeof record.error === "string") return record.error;
    if (typeof record.message === "string") return record.message;
  }
  return null;
}

function messageFromErrorContext(error: unknown): string | null {
  if (!error || typeof error !== "object" || !("context" in error)) return null;
  const context = (error as { context?: { body?: unknown } }).context;
  if (!context?.body) return null;
  return messageFromResponseBody(context.body);
}

function rawErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message.trim();
  if (typeof error === "string") return error.trim();
  return "";
}

function isTechnicalErrorMessage(message: string): boolean {
  if (!message) return true;
  return TECHNICAL_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

/**
 * Supabase FunctionsHttpError keeps the HTTP Response on `error.context`.
 * `functions.invoke` usually leaves `data` null on non-2xx, so callers should
 * await this and pass the result into formatEdgeFunctionError.
 */
export async function readFunctionsHttpErrorBody(error: unknown): Promise<unknown> {
  if (!error || typeof error !== "object" || !("context" in error)) return null;
  const context = (error as { context?: unknown }).context;
  if (!context || typeof context !== "object") return null;

  const response = context as {
    json?: () => Promise<unknown>;
    clone?: () => { json: () => Promise<unknown>; text?: () => Promise<string> };
    text?: () => Promise<string>;
    body?: unknown;
  };

  if (typeof response.clone === "function" && typeof response.json === "function") {
    try {
      return await response.clone().json();
    } catch {
      // fall through
    }
  }

  if (typeof response.json === "function") {
    try {
      return await response.json();
    } catch {
      // fall through
    }
  }

  if (typeof response.text === "function") {
    try {
      return await response.text();
    } catch {
      // fall through
    }
  }

  if ("body" in response) {
    return messageFromResponseBody(response.body) ? response.body : null;
  }

  return null;
}

/** Turn edge-function failures into short messages suitable for club admins. */
export function formatEdgeFunctionError(
  error: unknown,
  data?: unknown,
  fallback = "Something went wrong. Please try again.",
): string {
  const fromBody = messageFromResponseBody(data) ?? messageFromErrorContext(error);
  if (fromBody) return fromBody;

  const raw = rawErrorMessage(error);
  const fromRawJson = messageFromResponseBody(raw);
  if (fromRawJson) return fromRawJson;

  if (raw && !isTechnicalErrorMessage(raw)) return raw;

  if (isTechnicalErrorMessage(raw)) return fallback || UNAVAILABLE_MESSAGE;
  return fallback;
}
